import React, { useEffect, useState } from 'react';
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../../config/design/styles";
import {Chip, Drawer, IconButton, useMediaQuery} from '@mui/material';
import {
    PiggyBank,
    BadgePlus,
    Star,
    Home,
    ListTodo,
    ChevronDown,
    Sparkles,
    BadgePercent,
    PackageCheck,
    X
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import SubTitle from "../../../../components/elements/SubTitle";
import axios from "axios";
import { defaultVariables } from "../../../../../config";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Grid from "@mui/material/Grid2";
import Title from "../../../../components/elements/Title";
import Button from "@mui/material/Button";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

function FrontFilters({ classes, onFilterChange, industry, filter_data }) {
    const { t } = useTranslation();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(filter_data);
    const [catDrawOpen, setCatDraOpen] = useState(false);
    const navigate = useNavigate();

    const filters_empty = {
        "popular": false,
        "new": false,
        "economic": false,
        "pod": false,
        "categories": []
    };

    // Initialize filters from query params or defaults
    const [filters, setFilters] = useState(() => {
        const initialFilters = { ...filters_empty };
        for (const key in filters_empty) {
            if (key === "categories") {
                const categoriesParam = searchParams.get(key);
                initialFilters[key] = categoriesParam ? categoriesParam.split(',').map(Number) : [];
            } else if (searchParams.get(key) === "true") {
                initialFilters[key] = true;
            }
        }
        return initialFilters;
    });

    // Function to return only active filters or null if none
    const getActiveFilters = (filters) => {
        const activeFilters = Object.keys(filters).reduce((acc, key) => {
            if (key === "categories") {
                if (filters[key].length > 0) {
                    acc[key] = filters[key];
                }
            } else if (filters[key]) {
                acc[key] = true;
            }
            return acc;
        }, {});
        return Object.keys(activeFilters).length > 0 ? activeFilters : null;
    };

    // Check if there are any active filters
    const hasActiveFilters = getActiveFilters(filters) !== null;

    // Update query string and notify parent when filters change
    const changeFilter = (item, categoryId = null) => {
        setFilters((prevFilters) => {
            let updatedFilters;
            if (categoryId !== null) {
                const updatedCategories = [...prevFilters.categories];
                const categoryIndex = updatedCategories.indexOf(categoryId);
                if (categoryIndex > -1) {
                    updatedCategories.splice(categoryIndex, 1);
                } else {
                    updatedCategories.push(categoryId);
                }
                updatedFilters = {
                    ...prevFilters,
                    categories: updatedCategories,
                };
            } else {
                updatedFilters = {
                    ...prevFilters,
                    [item]: !prevFilters[item],
                };
            }

            const params = [];
            for (const key in updatedFilters) {
                if (key === "categories") {
                    if (updatedFilters[key].length > 0) {
                        params.push(`${key}=${updatedFilters[key].join(',')}`);
                    }
                } else if (updatedFilters[key]) {
                    params.push(`${key}=true`);
                }
            }
            const queryString = params.join('&');
            window.history.replaceState(null, '', `?${queryString}`);

            onFilterChange(getActiveFilters(updatedFilters));

            return updatedFilters;
        });
    };

    // Watch for changes in the query string and update filters
    useEffect(() => {
        const newFilters = { ...filters_empty };
        for (const key in filters_empty) {
            if (key === "categories") {
                const categoriesParam = searchParams.get(key);
                newFilters[key] = categoriesParam ? categoriesParam.split(',').map(Number) : [];
            } else if (searchParams.get(key) === "true") {
                newFilters[key] = true;
            }
        }
        setFilters(newFilters);
        onFilterChange(getActiveFilters(newFilters));
    }, [searchParams]);

    const clearAllFilters = () => {
        setFilters(filters_empty);
        window.history.replaceState(null, '', window.location.pathname);
        onFilterChange(null);
    };

    const Chipsets = () => (
        <>
            <Chip
                icon={<BadgePlus className="lucide-icon" />}
                label={t('new')}
                variant={filters.new ? "contianed" :"outlined"}
                color={filters.new ? "secondary" : "default"}
                className={filters.new ? "active" : ''}
                onClick={() => changeFilter('new')}
                onDelete={filters.new ? () => changeFilter('new') : undefined}
            />
            <Chip
                icon={<Sparkles className="lucide-icon" />}
                label={t('popular')}
                variant={filters.popular ? "contianed" :"outlined"}
                color={filters.popular ? "secondary" : "default"}
                className={filters.popular ? "active" : ''}
                onClick={() => changeFilter('popular')}
                onDelete={filters.popular ? () => changeFilter('popular') : undefined}
            />
            <Chip
                icon={<BadgePercent className="lucide-icon" />}
                label={t('economic')}
                variant={filters.economic ? "contianed" :"outlined"}
                color={filters.economic ? "secondary" : "default"}
                className={filters.economic ? "active" : ''}
                onClick={() => changeFilter('economic')}
                onDelete={filters.economic ? () => changeFilter('economic') : undefined}
            />
            <Chip
                icon={<PackageCheck className="lucide-icon" />}
                label={t('pod')}
                variant={filters.pod ? "contianed" :"outlined"}
                color={filters.pod ? "secondary" : "default"}
                className={filters.pod ? "active" : ''}
                onClick={() => changeFilter('pod')}
                onDelete={filters.pod ? () => changeFilter('pod') : undefined}
            />
        </>
    );

    const CategoryList = () => (
        <List>
            {data?.categories?.map((category) => {
                const labelId = `checkbox-list-label-${category.id}`;
                const isChecked = filters.categories.includes(category.id);

                return (
                    <ListItem
                        key={category.id}
                        role={undefined}
                        dense
                        onClick={() => changeFilter('categories', category.id)}
                    >
                        <Checkbox
                            edge="start"
                            checked={isChecked}
                            tabIndex={-1}
                            disableRipple
                            color="secondary"
                            inputProps={{ 'aria-labelledby': labelId }}
                        />
                        <ListItemText id={labelId} primary={category.name} style={{ cursor: 'pointer' }} />
                    </ListItem>
                );
            })}
        </List>
    );

    return (
        <>
            {!isMobile ? (
                <>
                    <div className="filters-holder-list">
                        <div className="title-holder">
                            <Title align="left" color="text" className="filter-title">
                                {t('filters')}
                            </Title>
                            {hasActiveFilters && (
                                <Button
                                    className="btn-clear"
                                    size="small"
                                    color="grayDark"
                                    variant="outlined"
                                    endIcon={<X className="lucide-small-narrow" />}
                                    onClick={() => clearAllFilters()}
                                >
                                    {t('clear')}
                                </Button>
                            )}
                        </div>
                        <div className="filter-section">
                            <SubTitle className="filter-subtitle mb-10" align="left">{t('quickFilters')}</SubTitle>
                            <div className='filter-chips-holder '>
                                <Chipsets />
                            </div>
                        </div>
                        <div className="filter-section">
                            <SubTitle align='left' className="filter-subtitle">{t('categories')}</SubTitle>
                            <CategoryList />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="filters-mobile">
                        <div className="chips-holder filters-holder">
                            <Chip
                                icon={<ListTodo className="lucide-icon" />}
                                label={
                                    filters.categories.length > 0
                                        ? `${t('categories')}: ${filters.categories
                                            .map((catId) => {
                                                const category = data.categories.find((cat) => cat.id === catId);
                                                return category ? category.name : '';
                                            })
                                            .join(', ')}`
                                        : t('categories')
                                }
                                variant={!!filters.categories.length ? "contianed" :"outlined"}
                                color={!!filters.categories.length ? "secondary" : "default"}
                                className={!!filters.categories.length ? "active" : ''}
                                onClick={() => setCatDraOpen(true)}
                                onDelete={() => setCatDraOpen(true)}
                                deleteIcon={<ChevronDown className="lucide-icon" />}
                            />
                            <Chipsets />
                        </div>
                    </div>
                    <Drawer
                        anchor={"bottom"}
                        open={catDrawOpen}
                        onClose={() => setCatDraOpen(false)}
                        className="filter-categories-drawer"
                    >
                        <Grid container spacing={2}>
                            <Grid item size={12} style={{position:"relative"}}>
                                <SubTitle align='left' className="filter-subtitle">{t('categories')}</SubTitle>
                                <IconButton
                                    onClick={() => setCatDraOpen(false)}
                                    size="small"
                                    className={classes.drawerClose}
                                >
                                    <CloseRoundedIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                            <Grid item size={12}>
                                <CategoryList />
                            </Grid>
                            <Grid item size={12}>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => setCatDraOpen(false)}
                                    style={{width:"100%"}}
                                >
                                    {t('doFilters')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Drawer>
                </>
            )}
        </>
    );
}

export default withStyles(Styles)(FrontFilters);